import React, { useState, useEffect } from "react";
import { Table, Button, Icon, Popup } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Accident, Division, AccidentType, Sector, Employee, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useSite, useCompany, useLanguage } from "../../../../contexts";
import { AccidentForm } from "../AccidentForm/AccidentForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListAccidents.scss";

const accidentController = new Accident();
const divisionCtrl      = new Division();
const accidentTypeCtrl  = new AccidentType();
const sectorCtrl        = new Sector();
const employeeCtrl      = new Employee();
const siteCtrl          = new Site();

const TODAY      = new Date().toISOString().slice(0, 10);
const TIME_NOW   = () => new Date().toTimeString().slice(0, 5);
const QUICK_INIT = () => ({ division: "", accidentType: "", sector: "", lugar: "", employee: "", legajo: "", date: TODAY, time: TIME_NOW() });

const CY = new Date().getFullYear();
const YEAR_OPTS = [CY, CY - 1, CY - 2];

const BODY_GROUPS = {
  "Cabeza / Cuello":  ["Cabeza", "Cara", "Ojos", "Cuello"],
  "Tronco / Espalda": ["Tronco/Espalda", "Hombros", "Caderas"],
  "Miembro superior": ["Brazos", "Manos"],
  "Miembro inferior": ["Piernas", "Rodillas", "Tobillos", "Pies"],
};
const getBodyGroup = (part) => {
  for (const [label, parts] of Object.entries(BODY_GROUPS)) {
    if (parts.includes(part)) return label;
  }
  return part;
};
const fmtBodyParts = (bodyParts) => {
  if (!bodyParts?.length) return "-";
  const groups = [...new Set(bodyParts.map(getBodyGroup))];
  return groups.length === 1 ? groups[0] : `${groups[0]} (+${groups.length - 1})`;
};

const fmtId = (a) =>
  a.customId && a.date
    ? `AT-${new Date(a.date).getFullYear()}-${String(a.customId).padStart(4, "0")}`
    : null;

const fmtDateES = (d) =>
  d ? new Date(d).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";

export function ListAccidents({ accidentsActive, reload, onReload, triggerNew }) {
  const { accessToken } = useAuth();
  const { site: siteFromContext } = useSite();
  const { company: companyFromContext } = useCompany();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const noSiteInCtx    = !siteFromContext    || siteFromContext    === "0" || siteFromContext    === 0;
  const noCompanyInCtx = !companyFromContext || companyFromContext === "0" || companyFromContext === 0;

  const CTYPE_OPTS = [
    { value: "",                  label: t("all_types") },
    { value: "no_injury",         label: t("case_type_no_injury") },
    { value: "first_aid",         label: t("case_type_first_aid") },
    { value: "medical_treatment", label: t("case_type_medical_treatment") },
    { value: "lost_time",         label: t("case_type_lost_time") },
    { value: "fatality",          label: t("case_type_fatality") },
  ];
  const CTYPE_LABELS = {
    no_injury:         t("case_type_no_injury"),
    first_aid:         t("case_type_first_aid"),
    medical_treatment: t("case_type_medical_treatment"),
    lost_time:         t("case_type_lost_time"),
    fatality:          t("case_type_fatality"),
  };
  const CASE_STATUS_META = {
    draft:    { label: t("case_status_draft"),    color: "#6B7280", bg: "#F3F4F6" },
    open:     { label: t("case_status_open"),     color: "#1D4ED8", bg: "#DBEAFE" },
    plan:     { label: t("case_status_plan"),     color: "#B45309", bg: "#FEF3C7" },
    closed:   { label: t("case_status_closed"),   color: "#15803D", bg: "#DCFCE7" },
    reopened: { label: t("case_status_reopened"), color: "#B91C1C", bg: "#FEE2E2" },
  };
  const translateBodyGroup = (group) => ({
    "Cabeza / Cuello":  t("body_group_head_neck"),
    "Tronco / Espalda": t("body_group_trunk_back"),
    "Miembro superior": t("body_group_upper_limb"),
    "Miembro inferior": t("body_group_lower_limb"),
  }[group] || group);
  const fmtBodyPartsI18n = (bodyParts) => {
    if (!bodyParts?.length) return "-";
    const groups = [...new Set(bodyParts.map(getBodyGroup))];
    const first = translateBodyGroup(groups[0]);
    return groups.length === 1 ? first : `${first} (+${groups.length - 1})`;
  };

  const [accidents,       setAccidents]       = useState(null);
  const [accidentsFilter, setAccidentsFilter] = useState([]);
  const [search,          setSearch]          = useState("");
  const [filterTipo,      setFilterTipo]      = useState("");
  const [filterYear,      setFilterYear]      = useState(String(CY));
  const [siteFilter,      setSiteFilter]      = useState("");
  const [sites,           setSites]           = useState([]);
  const [companySiteIds,  setCompanySiteIds]  = useState([]);
  const [showModal,       setShowModal]       = useState(false);
  const [selectedItem,    setSelectedItem]    = useState(null);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [confirmMsg,      setConfirmMsg]      = useState("");
  const [isDelete,        setIsDelete]        = useState(false);

  // Catálogos para barra rápida
  const [divisions,     setDivisions]     = useState([]);
  const [accidentTypes, setAccidentTypes] = useState([]);
  const [sectors,       setSectors]       = useState([]);
  const [employees,     setEmployees]     = useState([]);

  // Barra rápida de creación
  const [quick,       setQuick]       = useState(QUICK_INIT());
  const [quickSaving, setQuickSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setAccidents(null);
        const res = await accidentController.getAccidents(accessToken, accidentsActive);
        setAccidents(res);
        setAccidentsFilter(res);
      } catch {
        setAccidents([]);
        setAccidentsFilter([]);
      }
    })();
  }, [accidentsActive, reload]);

  useEffect(() => {
    if (!accessToken) return;
    const load = !noCompanyInCtx
      ? siteCtrl.getSitesByCompany(accessToken, companyFromContext, true)
      : siteCtrl.getSites(accessToken, true);
    load.then(res => setSites(Array.isArray(res) ? res : [])).catch(() => {});
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  useEffect(() => { setSiteFilter(""); }, [siteFromContext]);

  useEffect(() => {
    if (noCompanyInCtx) { setCompanySiteIds([]); return; }
    siteCtrl.getSitesByCompany(accessToken, companyFromContext, true)
      .then(res => setCompanySiteIds((res || []).map(s => s._id)))
      .catch(() => setCompanySiteIds([]));
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  useEffect(() => {
    if (triggerNew) openCreate();
  }, [triggerNew]);

  // Carga catálogos para barra rápida (una sola vez)
  useEffect(() => {
    (async () => {
      try {
        const [divs, types, sects, emps] = await Promise.all([
          divisionCtrl.getDivisions(accessToken, true),
          accidentTypeCtrl.getAccidentTypes(accessToken, true),
          sectorCtrl.getSectors(accessToken, true),
          employeeCtrl.getEmployees(accessToken, { active: true }),
        ]);
        setDivisions(divs  || []);
        setAccidentTypes(types || []);
        setSectors(sects  || []);
        setEmployees(emps  || []);
      } catch { /* catálogos no críticos */ }
    })();
  }, [accessToken]);

  useEffect(() => {
    if (!accidents) return;
    let filtered = [...accidents];

    if (!noSiteInCtx) {
      filtered = filtered.filter(a => (a.site?._id || a.site) === siteFromContext);
    } else if (!noCompanyInCtx && companySiteIds.length > 0) {
      filtered = filtered.filter(a => companySiteIds.includes(a.site?._id || a.site));
      if (siteFilter) filtered = filtered.filter(a => (a.site?._id || a.site) === siteFilter);
    } else if (siteFilter) {
      filtered = filtered.filter(a => (a.site?._id || a.site) === siteFilter);
    }

    if (filterYear) {
      filtered = filtered.filter(
        a => a.date && new Date(a.date).getFullYear() === Number(filterYear)
      );
    }
    if (filterTipo) {
      filtered = filtered.filter(a => a.caseType === filterTipo);
    }
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(a => {
        const id = fmtId(a);
        return (
          (id && id.toLowerCase().includes(q)) ||
          a.employee?.toLowerCase().includes(q) ||
          a.site?.name?.toLowerCase().includes(q) ||
          fmtDateES(a.date).includes(q) ||
          a.legajo?.toLowerCase().includes(q) ||
          a.place?.toLowerCase().includes(q)
        );
      });
    }

    setAccidentsFilter(filtered);
  }, [search, filterTipo, filterYear, siteFilter, siteFromContext, noSiteInCtx, noCompanyInCtx, companySiteIds, accidents]);

  const openCreate = () => { setSelectedItem(null); setShowModal(true); };
  const openEdit   = (a)  => { setSelectedItem(a);   setShowModal(true); };

  const openToggle = (a) => {
    setSelectedItem(a);
    setIsDelete(false);
    setConfirmMsg(`¿${a.active ? t("deactivate") : t("activate")} "${a.employee}"?`);
    setShowConfirm(true);
  };

  const openDelete = (a) => {
    setSelectedItem(a);
    setIsDelete(true);
    setConfirmMsg(`¿${t("delete")} "${a.employee}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await accidentController.updateAccident(accessToken, selectedItem._id, { active: !selectedItem.active });
      toast.success(selectedItem.active ? t("accident_deactivated") : t("accident_activated"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("error_updating")); }
  };

  const handleDelete = async () => {
    try {
      await accidentController.deleteAccident(accessToken, selectedItem._id);
      toast.success(t("accident_deleted"));
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error_deleting")); }
  };

  const setQ = (f, v) => setQuick(p => ({ ...p, [f]: v }));

  // Legajo → auto-complete nombre del empleado
  const handleLegajoBlur = () => {
    if (!quick.legajo.trim()) return;
    const found = employees.find(
      e => e.numero_legajo?.trim().toLowerCase() === quick.legajo.trim().toLowerCase()
    );
    if (found) setQ("employee", `${found.nombre} ${found.apellido}`.trim());
  };

  const handleQuickSave = async () => {
    if (!quick.date) return toast.warning(t("date_required"));
    setQuickSaving(true);
    try {
      const payload = {
        date:         quick.date,
        time:         quick.time,
        division:     quick.division     || undefined,
        accidentType: quick.accidentType || undefined,
        sector:       quick.sector       || undefined,
        location:     quick.lugar        || undefined,
        employee:     quick.employee     || undefined,
        legajo:       quick.legajo       || undefined,
        caseStatus:   "open",
      };
      const result  = await accidentController.createAccident(accessToken, payload);
      const created = result?.accident || result;
      toast.success(t("accident_created_complete_form"));
      setQuick(QUICK_INIT());
      onReload();
      // Abre el formulario completo con el registro recién creado
      setSelectedItem(created);
      setShowModal(true);
    } catch (err) {
      toast.error(err?.message || t("error_creating_accident"));
    } finally {
      setQuickSaving(false);
    }
  };

  const formatDate = (d) => fmtDateES(d) || "-";

  if (accidents === null) return <LoadingSpinner />;

  return (
    <div className="accidents-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="acc-search-bar">
        <div className="search-user-wrapper acc-search-bar__input">
          <img src={searchSvg} alt="search" className="search-user-icon" />
          <input
            className="search-user-input"
            placeholder={t("search_accidents_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="acc-search-bar__pills">
          <select
            className="acc-pill"
            value={filterTipo}
            onChange={e => setFilterTipo(e.target.value)}
          >
            {CTYPE_OPTS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <select
            className="acc-pill"
            value={filterYear}
            onChange={e => setFilterYear(e.target.value)}
          >
            <option value="">{t("all_years")}</option>
            {YEAR_OPTS.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          {noSiteInCtx && sites.length > 0 && (
            <select
              className="acc-pill"
              value={siteFilter}
              onChange={e => setSiteFilter(e.target.value)}
            >
              <option value="">{t("all_sites")}</option>
              {sites.map(s => (
                <option key={s._id} value={s._id}>{s.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* ── BARRA RÁPIDA DE CREACIÓN ── */}
      <div className="acc-quick-bar">
        <select className="acc-quick-sel" value={quick.division} onChange={e => setQ("division", e.target.value)}>
          <option value="">{t("division")}</option>
          {divisions.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
        </select>

        <select className="acc-quick-sel" value={quick.accidentType} onChange={e => setQ("accidentType", e.target.value)}>
          <option value="">{t("accident_type")}</option>
          {accidentTypes.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
        </select>

        <select className="acc-quick-sel" value={quick.sector} onChange={e => setQ("sector", e.target.value)}>
          <option value="">{t("sector")}</option>
          {sectors.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>

        <input className="acc-quick-inp" placeholder={t("place")} value={quick.lugar}
          onChange={e => setQ("lugar", e.target.value)} />

        <input className="acc-quick-inp" placeholder={t("employee")} value={quick.employee}
          onChange={e => setQ("employee", e.target.value)} />

        <input className="acc-quick-inp acc-quick-inp--sm" placeholder={t("legajo")} value={quick.legajo}
          onChange={e => setQ("legajo", e.target.value)}
          onBlur={handleLegajoBlur} />

        <input className="acc-quick-inp acc-quick-inp--sm" type="date" value={quick.date}
          onChange={e => setQ("date", e.target.value)} />

        <input className="acc-quick-inp acc-quick-inp--sm" type="time" value={quick.time}
          onChange={e => setQ("time", e.target.value)} />

        <div className="acc-quick-actions">
          <button className="acc-quick-btn acc-quick-btn--save" onClick={handleQuickSave} disabled={quickSaving} title="Guardar">
            ✓
          </button>
          <button className="acc-quick-btn acc-quick-btn--cancel" onClick={() => setQuick(QUICK_INIT())} title="Cancelar">
            ✕
          </button>
        </div>
      </div>

      {accidentsFilter.length === 0 ? (
        <p style={{ color: "#999" }}>{t("no_results")}</p>
      ) : (
        <Table className="accidents-table" selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>ID</Table.HeaderCell>
              <Table.HeaderCell>{t("date")}</Table.HeaderCell>
              <Table.HeaderCell>{t("site")}</Table.HeaderCell>
              <Table.HeaderCell>{t("division")}</Table.HeaderCell>
              <Table.HeaderCell>{t("type")}</Table.HeaderCell>
              <Table.HeaderCell>{t("mechanism")}</Table.HeaderCell>
              <Table.HeaderCell>{t("body_part")}</Table.HeaderCell>
              <Table.HeaderCell>{t("employee")}</Table.HeaderCell>
              <Table.HeaderCell>{t("days")}</Table.HeaderCell>
              <Table.HeaderCell>{t("status")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">{t("actions")}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {accidentsFilter.map((a) => {
              const id = fmtId(a);
              const statusMeta = CASE_STATUS_META[a.caseStatus] || CASE_STATUS_META.draft;
              return (
                <Table.Row key={a._id}>
                  <Table.Cell>
                    <span className="acc-id-label">{id || `#${String(a._id).slice(-6).toUpperCase()}`}</span>
                  </Table.Cell>
                  <Table.Cell>{formatDate(a.date)}</Table.Cell>
                  <Table.Cell>{a.site?.name     || "-"}</Table.Cell>
                  <Table.Cell>{a.division?.name || "-"}</Table.Cell>
                  <Table.Cell>{CTYPE_LABELS[a.caseType] || "-"}</Table.Cell>
                  <Table.Cell>{a.accidentType?.name || "-"}</Table.Cell>
                  <Table.Cell>{fmtBodyPartsI18n(a.bodyParts)}</Table.Cell>
                  <Table.Cell>{a.employee || "-"}</Table.Cell>
                  <Table.Cell>{a.lostDays ?? "-"}</Table.Cell>
                  <Table.Cell>
                    <span
                      className="acc-status-pill"
                      style={{ color: statusMeta.color, background: statusMeta.bg }}
                    >
                      {statusMeta.label}
                    </span>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Popup content={t("view")} trigger={
                      <Button icon size="mini" onClick={() => openEdit(a)}>
                        <Icon name="eye" />
                      </Button>
                    } />
                    <Popup content={t("edit")} trigger={
                      <Button icon primary size="mini" onClick={() => openEdit(a)}>
                        <Icon name="pencil" />
                      </Button>
                    } />
                    <Popup content={t("delete")} trigger={
                      <Button icon size="mini" color="red" onClick={() => openDelete(a)}>
                        <Icon name="trash" />
                      </Button>
                    } />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selectedItem ? `${t("edit")}: ${selectedItem.employee || ""}` : t("new_accident")}
        size="large"
      >
        <AccidentForm
          accident={selectedItem}
          close={() => setShowModal(false)}
          onReload={onReload}
        />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? t("delete") : (selectedItem?.active ? t("deactivate") : t("activate"))}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel={t("accept")}
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
